import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Drawer, Divider, IconButton, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

export default function FileUploadLayout({
  open,
  setUploadFileStatus,
  uploadFileProgressData,
  attachments,
  progressRefs = [],
  uploadFileStatus,
  onDownloadComplete=()=>{}
}) {
  const [progressValues, setProgressValues] = useState(
    Array(attachments?.length).fill(0)
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const prevSuccessCountRef = useRef(null);

  useEffect(() => {
    if (progressRefs && progressRefs.current) {
      progressRefs.current =
        uploadFileProgressData?.response?.data?.data?.successful_files?.map(
          (_, i) => {
            return (value) => {
              setProgressValues((prev) => {
                const updated = [...prev];
                updated[i] = value;
                return updated;
              });
            };
          }
        );
    }
  }, [attachments, uploadFileProgressData]);

  const successFiles = useMemo(() => {
    return (
      uploadFileProgressData?.response?.data?.data?.successful_files
        ?.map((file, i) => ({
          file,
          progress: progressValues[i] || 0
        }))
        .filter((item) => item.progress < 100) || []
    );
  }, [attachments, progressValues, uploadFileProgressData]);

  const failedFiles = useMemo(() => {
    if(uploadFileStatus?.data){
      return uploadFileStatus?.data?.failed_files;
    }else{
      return uploadFileProgressData?.response?.data?.data?.failed_files 
    }
  }, [uploadFileProgressData,uploadFileStatus?.data]);

  const currentFile = successFiles?.[currentIndex] || null;

  useEffect(() => {
    if (successFiles?.length > 0) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue =
          "File upload progress will be lost. Are you sure you want to leave?";
        return e.returnValue;
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [successFiles]);

useEffect(() => {
  const currentCount = successFiles.length;
  if (prevSuccessCountRef.current > 0 && currentCount === 0) onDownloadComplete();
  prevSuccessCountRef.current = currentCount;
}, [successFiles.length]);

 

  return (
    <div>
      <Drawer anchor="right" open={open} className="add-lease-dialog" maxWidth="md" fullWidth>
        {/* Header */}
        <Box
          sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            color: "#000000",
            height:"49px"
          }}
        >
          <Typography variant="subtitle1" ml="8px">Attachment(s) Upload Progress</Typography>
          <IconButton
            size="small"
            onClick={() => setUploadFileStatus({ modal: false, data: null, mode: "view" })}
            sx={{ color: "#000000" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: "10px" }}>
          {/* Uploading Files */}
          {successFiles?.length > 0 && <Typography fontSize="12px">Uploading...</Typography>}
          {successFiles?.length > 0 && (
            <List>
              {successFiles.map((item, index) => (
                <ListItem
                  key={index}
                  sx={{
                    flexDirection: "column", alignItems: "flex-start", mb: 2,
                    border: "1px solid", borderColor: "grey.300", borderRadius: 2,
                    p: "8px", boxShadow: 1, bgcolor: "background.paper"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: "primary.main" }}>
                      <InsertDriveFileIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight="500">{item?.file?.file_name}</Typography>}
                      secondary={`${item?.progress || 0}%`}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item?.progress || 0}
                    sx={{ width: "100%", borderRadius: 1, height: 6 }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Failed Files */}
          {failedFiles?.length > 0 && <Typography fontSize="12px">Uploading Failed during Upload...</Typography>}
          {failedFiles?.length > 0 && (
            <List>
              {failedFiles.map((item, index) => (
                <ListItem
                  key={index}
                  sx={{
                    flexDirection: "column", alignItems: "flex-start", mb: 2,
                    border: "1px solid", borderColor: "grey.300", borderRadius: 2,
                    p: "8px", boxShadow: 1, bgcolor: "background.paper"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 32, color: "primary.main" }}>
                      <CloseIcon fontSize="small"  color="error"/>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight="500">{item?.file_name}</Typography>}
                      secondary={<Typography variant="body2" fontWeight="500" color="error">{item?.error_message}</Typography>}
                    />
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
           {successFiles?.length === 0 && failedFiles?.length === 0 && (
            <Typography fontSize="12px" color="text.secondary">
              No file remaining for upload
            </Typography>
          )}
        </Box>
      </Drawer>
    </div>
  );
}
